import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../enviroment';

/**
 * Legacy compatibility API service.
 * Prefer core/services/api.service.ts for new code.
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getEmployees(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/Employees`);
  }

  exportEmployeesCsv(department?: string): Observable<Blob> {
    let url = `${this.apiUrl}/v1/Employees/export`;
    if (department && department !== 'All') {
      url += `?department=${encodeURIComponent(department)}`;
    }
    return this.http.get(url, { responseType: 'blob' });
  }

  importEmployeesCsv(file: File, department?: string): Observable<any> {
    const form = new FormData();
    form.append('file', file, file.name);
    if (department && department !== 'All') {
      form.append('department', department);
    }
    return this.http.post<any>(`${this.apiUrl}/v1/Employees/import`, form);
  }

  getEmployeeById(id: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/Employees/${id}`);
  }

  updateEmployeeProfile(id: number | string, profile: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/v1/Employees/${id}/profile`, profile);
  }

  uploadEmployeePhoto(id: number | string, file: Blob, fileName: string): Observable<any> {
    const form = new FormData();
    form.append('file', file, fileName);
    return this.http.post<any>(`${this.apiUrl}/v1/Employees/${id}/photo`, form);
  }

  deleteEmployeePhoto(id: number | string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/v1/Employees/${id}/photo`);
  }

  getEmployeePhotoBlob(id: number | string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/v1/Employees/${id}/photo`, { responseType: 'blob' });
  }

  getEmployeeDocumentCenter(employeeId: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/employees/${employeeId}/documents`);
  }

  uploadEmployeeDocument(
    employeeId: number | string,
    file: File,
    meta: { documentTypeId: string; customTitle?: string; expiryDate?: string; remarks?: string }
  ): Observable<any> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('documentTypeId', meta.documentTypeId);
    if (meta.customTitle) form.append('customTitle', meta.customTitle);
    if (meta.expiryDate) form.append('expiryDate', meta.expiryDate);
    if (meta.remarks) form.append('remarks', meta.remarks);
    return this.http.post<any>(`${this.apiUrl}/v1/employees/${employeeId}/documents`, form);
  }

  replaceEmployeeDocument(
    employeeId: number | string,
    documentId: string,
    file: File,
    remarks?: string
  ): Observable<any> {
    const form = new FormData();
    form.append('file', file, file.name);
    if (remarks) form.append('remarks', remarks);
    return this.http.post<any>(`${this.apiUrl}/v1/employees/${employeeId}/documents/${documentId}/replace`, form);
  }

  deleteEmployeeDocument(employeeId: number | string, documentId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/v1/employees/${employeeId}/documents/${documentId}`);
  }

  verifyEmployeeDocument(employeeId: number | string, documentId: string, isVerified: boolean, remarks?: string, reject = false): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/employees/${employeeId}/documents/${documentId}/verify`, {
      isVerified,
      remarks,
      reject
    });
  }

  getEmployeeDocumentVersions(employeeId: number | string, documentId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/employees/${employeeId}/documents/${documentId}/versions`);
  }

  downloadEmployeeDocument(employeeId: number | string, documentId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/v1/employees/${employeeId}/documents/${documentId}/file`, {
      responseType: 'blob'
    });
  }

  previewEmployeeDocument(employeeId: number | string, documentId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/v1/employees/${employeeId}/documents/${documentId}/file`, {
      params: { inline: 'true' },
      responseType: 'blob'
    });
  }

  downloadEmployeeDocumentVersion(employeeId: number | string, documentId: string, versionId: string): Observable<Blob> {
    return this.http.get(
      `${this.apiUrl}/v1/employees/${employeeId}/documents/${documentId}/versions/${versionId}/file`,
      { responseType: 'blob' }
    );
  }

  createEmployee(employee: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/EmployeeForm`, employee);
  }

  updateEmployee(id: number | string, employee: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/EmployeeForm/${id}`, employee);
  }

  deleteEmployee(id: number | string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/EmployeeForm/${id}`);
  }

  getEmployeeFormData(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/Employees/form-data`);
  }

  getHREmployees(pageNumber: number = 1, pageSize: number = 10, searchTerm: string = ''): Observable<any> {
    let url = `${this.apiUrl}/v1/Employees?page=${pageNumber}&pageSize=${pageSize}`;
    if (searchTerm) {
      url += `&search=${encodeURIComponent(searchTerm)}`;
    }
    return this.http.get<any>(url);
  }

  createHREmployee(employee: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/Employees`, employee);
  }

  updateHREmployee(id: number | string, employee: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/v1/Employees/${id}`, employee);
  }

  deleteHREmployee(id: number | string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/v1/Employees/${id}`);
  }

  getHRDepartments(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/Departments`);
  }

  createHRDepartment(department: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/Departments`, department);
  }

  updateHRDepartment(id: number | string, department: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/v1/Departments/${id}`, department);
  }

  deleteHRDepartment(id: number | string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/v1/Departments/${id}`);
  }

  getHRDepartmentById(id: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/Departments/${id}`);
  }

  getHRDepartmentEmployees(id: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/Departments/${id}/employees`);
  }

  getDashboardStats(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/dashboard`);
  }

  getAttendance(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/Attendance`);
  }

  getAttendanceRecords(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/Attendance`);
  }

  getAttendanceReconciliation(from: string, to: string, employeeId?: string): Observable<any> {
    let url = `${this.apiUrl}/v1/Attendance/reconciliation?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    if (employeeId) {
      url += `&employeeId=${encodeURIComponent(employeeId)}`;
    }
    return this.http.get<any>(url);
  }

  updateAttendanceReconciliation(items: any[]): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/v1/Attendance/reconciliation`, {
      items
    });
  }

  exportAttendanceReconciliationCsv(from: string, to: string, employeeId?: string): Observable<Blob> {
    let url = `${this.apiUrl}/v1/Attendance/reconciliation/export?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    if (employeeId) {
      url += `&employeeId=${encodeURIComponent(employeeId)}`;
    }
    return this.http.get(url, { responseType: 'blob' });
  }

  exportAttendanceCsv(from: string, to: string, employeeId?: string): Observable<Blob> {
    let url = `${this.apiUrl}/v1/Attendance/export?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    if (employeeId) {
      url += `&employeeId=${encodeURIComponent(employeeId)}`;
    }
    return this.http.get(url, { responseType: 'blob' });
  }

  importAttendanceCsv(file: File, from?: string, to?: string): Observable<any> {
    const form = new FormData();
    form.append('file', file, file.name);
    if (from) form.append('from', from);
    if (to) form.append('to', to);
    return this.http.post<any>(`${this.apiUrl}/v1/Attendance/import`, form);
  }

  getAttendanceById(id: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/Attendance/${id}`);
  }

  createAttendance(attendance: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/Attendance`, attendance);
  }

  updateAttendance(id: number | string, attendance: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/v1/Attendance/${id}`, attendance);
  }

  getEmployeeAttendance(employeeId: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/Attendance?employeeId=${employeeId}`);
  }

  importEmployeeAttendanceCsv(
    employeeId: number | string,
    file: File,
    from?: string,
    to?: string
  ): Observable<any> {
    const form = new FormData();
    form.append('file', file, file.name);
    if (from) form.append('from', from);
    if (to) form.append('to', to);
    return this.http.post<any>(`${this.apiUrl}/v1/employees/${employeeId}/attendance/import`, form);
  }

  exportEmployeeAttendanceCsv(employeeId: number | string, from: string, to: string): Observable<Blob> {
    const url =
      `${this.apiUrl}/v1/employees/${employeeId}/attendance/export` +
      `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  getEmployeeAttendanceImports(employeeId: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/employees/${employeeId}/attendance/imports`);
  }

  getEmployeeAttendanceSummary(
    employeeId: number | string,
    params: {
      periodType?: string | number;
      anchorDate?: string;
      from?: string;
      to?: string;
      persist?: boolean;
    } = {}
  ): Observable<any> {
    const q = new URLSearchParams();
    if (params.periodType !== undefined) q.set('periodType', String(params.periodType));
    if (params.anchorDate) q.set('anchorDate', params.anchorDate);
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.persist !== undefined) q.set('persist', String(params.persist));
    const qs = q.toString();
    return this.http.get<any>(
      `${this.apiUrl}/v1/employees/${employeeId}/attendance/summary${qs ? `?${qs}` : ''}`
    );
  }

  getEmployeeAttendanceCalendar(employeeId: number | string, from: string, to: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}/v1/employees/${employeeId}/attendance/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    );
  }

  getAttendanceReport(periodType: string | number, anchorDate?: string, employeeId?: string): Observable<any> {
    const q = new URLSearchParams();
    q.set('periodType', String(periodType));
    if (anchorDate) q.set('anchorDate', anchorDate);
    if (employeeId) q.set('employeeId', employeeId);
    return this.http.get<any>(`${this.apiUrl}/v1/Attendance/report?${q.toString()}`);
  }

  getAttendanceStats(employeeId?: number, month?: number, year?: number): Observable<any> {
    let url = `${this.apiUrl}/AttendanceForm/stats`;
    const params: string[] = [];
    if (employeeId !== undefined) params.push(`employeeId=${employeeId}`);
    if (month !== undefined) params.push(`month=${month}`);
    if (year !== undefined) params.push(`year=${year}`);
    if (params.length > 0) url += '?' + params.join('&');
    return this.http.get<any>(url);
  }

  checkIn(attendance: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/AttendanceForm/checkin`, attendance);
  }

  checkOut(attendance: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/AttendanceForm/checkout`, attendance);
  }

  getLeaveRequests(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/leave-requests`);
  }

  getLeaveRequestById(id: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/leave-requests/${id}`);
  }

  createLeaveRequest(leave: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/leave-requests`, leave);
  }

  cancelLeaveRequest(id: number | string, remarks?: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/leave-requests/${id}/cancel`, { remarks });
  }

  getLeaveBalanceV1(employeeId: number | string, year?: number): Observable<any> {
    const qs = year ? `?year=${year}` : '';
    return this.http.get<any>(`${this.apiUrl}/v1/leave-requests/balance/${employeeId}${qs}`);
  }

  getLeaveCalendar(from: string, to: string, employeeId?: string): Observable<any> {
    let url = `${this.apiUrl}/v1/leave-requests/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    if (employeeId) url += `&employeeId=${encodeURIComponent(employeeId)}`;
    return this.http.get<any>(url);
  }

  getLeaveTeamAvailability(from: string, to: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}/v1/leave-requests/team-availability?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    );
  }

  getEmployeeTimeline(employeeId: number | string, take = 100): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/employees/${employeeId}/timeline?take=${take}`);
  }

  addEmployeeTimelineEvent(employeeId: number | string, body: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/employees/${employeeId}/timeline`, body);
  }

  updateLeaveRequest(id: number | string, leave: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/v1/leave-requests/${id}`, leave);
  }

  approveLeaveRequest(id: number | string, approvedBy: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/leave-requests/${id}/approve`, { remarks: null, approvedBy });
  }

  forwardLeaveRequest(id: number | string, remarks?: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/leave-requests/${id}/forward`, { remarks });
  }

  approveLeaveRequestWithRemarks(id: number | string, remarks?: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/leave-requests/${id}/approve`, { remarks });
  }

  rejectLeaveRequestWithRemarks(
    id: number | string,
    reason?: string,
    remarks?: string
  ): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/leave-requests/${id}/reject`, { reason, remarks });
  }

  rejectLeaveRequest(id: number | string, approvedBy: number, comments?: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/leave-requests/${id}/reject`, { approvedBy, comments, reason: comments, remarks: null });
  }

  getLeaveRequestsByEmployee(employeeId: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/leave-requests?employeeId=${employeeId}`);
  }

  getLeaveBalance(employeeId: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/LeaveRequestForm/balance/${employeeId}`);
  }

  getPerformanceReviews(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/performance`);
  }

  getPerformanceReviewById(id: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/performance/${id}`);
  }

  createPerformanceReview(review: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/performance`, review);
  }

  updatePerformanceReview(id: number | string, review: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/v1/performance/${id}`, review);
  }

  deletePerformanceReview(id: number | string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/v1/performance/${id}`);
  }

  getPerformanceReviewsByEmployee(employeeId: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/performance?employeeId=${employeeId}`);
  }

  getPerformanceStats(employeeId?: number, reviewPeriod?: string): Observable<any> {
    let url = `${this.apiUrl}/PerformanceReviewForm/stats`;
    const params: string[] = [];
    if (employeeId !== undefined) params.push(`employeeId=${employeeId}`);
    if (reviewPeriod) params.push(`reviewPeriod=${reviewPeriod}`);
    if (params.length > 0) url += '?' + params.join('&');
    return this.http.get<any>(url);
  }

  getTrainings(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/TrainingForm`);
  }

  getTrainingById(id: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/TrainingForm/${id}`);
  }

  createTraining(training: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/TrainingForm`, training);
  }

  updateTraining(id: number, training: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/TrainingForm/${id}`, training);
  }

  deleteTraining(id: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/TrainingForm/${id}`);
  }

  enrollEmployee(trainingId: number, enrollment: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/TrainingForm/${trainingId}/enroll`, enrollment);
  }

  getTrainingEnrollments(trainingId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/TrainingForm/${trainingId}/enrollments`);
  }

  updateEnrollmentStatus(enrollmentId: number, status: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/TrainingForm/enrollment/${enrollmentId}`, status);
  }

  getTrainingsByEmployee(employeeId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/TrainingForm/employee/${employeeId}`);
  }

  getDepartments(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/Departments`);
  }

  createDepartment(department: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/Departments`, department);
  }

  updateDepartment(id: number | string, department: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/v1/Departments/${id}`, department);
  }

  getDepartmentById(id: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/Departments/${id}`);
  }

  getDepartmentEmployees(id: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/Departments/${id}/employees`);
  }

  getPayroll(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/payroll`);
  }

  getReports(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/reports`);
  }
}
